import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const FORM_URL = 'https://readdy.ai/api/form/d8j8b6kcl43d0bibf6vg';

export default function Contact() {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (status === 'loading') return;
    setStatus('loading');
    try {
      const fd = new URLSearchParams();
      fd.append('name', form.name);
      fd.append('email', form.email);
      fd.append('phone', form.phone);
      fd.append('subject', form.subject);
      fd.append('message', form.message);
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: fd.toString(),
      });
      setStatus('success');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="pt-24 md:pt-32 pb-12 md:pb-16 bg-primary-900">
        <div className="max-w-7xl mx-auto px-4 md:px-6 text-center">
          <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50">{t('contact.title')}</h1>
          <p className="text-sm md:text-base text-background-50/70 mt-4 max-w-2xl mx-auto">{t('contact.subtitle')}</p>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">

            {/* Info */}
            <div className="w-full lg:w-2/5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">{t('contact.info')}</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-map-pin-line text-accent-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">{t('contact.address')}</p>
                    <p className="text-xs text-foreground-500">Bonanjo, Douala, Littoral, Cameroun</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-phone-line text-accent-600"></i>
                  </div>
                  <div>
                    <a href="tel:+237687696969" className="text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors cursor-pointer">{t('contact.phone1')}</a>
                    <br />
                    <a href="tel:+237675987654" className="text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors cursor-pointer">{t('contact.phone2')}</a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-mail-line text-accent-600"></i>
                  </div>
                  <a href="mailto:contact@tennisclubdouala.com" className="text-sm font-medium text-foreground-900 hover:text-accent-600 transition-colors cursor-pointer">{t('contact.email')}</a>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 flex-shrink-0">
                    <i className="ri-time-line text-accent-600"></i>
                  </div>
                  <p className="text-sm font-medium text-foreground-900">{t('contact.hours')}</p>
                </div>
              </div>

              <h3 className="font-heading text-lg font-semibold text-foreground-950 mt-8 mb-3">{t('contact.social')}</h3>
              <div className="flex gap-3">
                <a href="https://www.facebook.com/profile.php?id=61588341630517" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 cursor-pointer" aria-label="Facebook">
                  <i className="ri-facebook-fill"></i>
                </a>
                <a href="https://www.instagram.com/tennis_club_douala" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 cursor-pointer" aria-label="Instagram">
                  <i className="ri-instagram-line"></i>
                </a>
                <a href="https://wa.me/237687696969" target="_blank" rel="nofollow noopener noreferrer" className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 hover:bg-accent-500 hover:text-background-50 transition-all duration-300 cursor-pointer" aria-label="WhatsApp">
                  <i className="ri-whatsapp-line"></i>
                </a>
              </div>
            </div>

            {/* Form */}
            <div className="w-full lg:w-3/5">
              <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">{t('contact.form.title')}</h2>
              <form data-readdy-form onSubmit={handleSubmit} className="bg-background-100 rounded-2xl p-6 md:p-8 border border-background-200/70 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.form.name')}</label>
                    <input type="text" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.form.email')}</label>
                    <input type="email" name="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.form.phone')}</label>
                    <input type="tel" name="phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.form.subject')}</label>
                    <select name="subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors" required>
                      <option value="">--</option>
                      <option value="Adhésion">{t('nav.pricing')}</option>
                      <option value="Cours">{t('nav.activities')}</option>
                      <option value="Tournois">{t('nav.tournaments')}</option>
                      <option value="Annonceurs">{t('nav.advertisers')}</option>
                      <option value="Investisseurs">Investisseurs</option>
                      <option value="Autre">Autre</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-foreground-700 mb-1.5">{t('contact.form.message')}</label>
                  <textarea name="message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={5} maxLength={500} className="w-full px-4 py-2.5 rounded-lg bg-background-50 border border-foreground-200 text-foreground-900 text-sm focus:outline-none focus:border-accent-400 transition-colors resize-none" required></textarea>
                </div>
                <div className="flex items-center gap-3">
                  <button type="submit" disabled={status === 'loading'} className="px-8 py-3 bg-primary-500 text-background-50 font-semibold text-sm rounded-full hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap disabled:opacity-60">
                    {status === 'loading' ? '...' : t('contact.form.cta')}
                  </button>
                  {status === 'success' && <span className="text-sm text-green-600 font-medium">{t('contact.form.success')}</span>}
                  {status === 'error' && <span className="text-sm text-red-500 font-medium">{t('contact.form.error')}</span>}
                </div>
              </form>
            </div>
          </div>

          {/* Map */}
          <div className="mt-12">
            <h2 className="font-heading text-2xl font-bold text-foreground-950 mb-6">{t('contact.map')}</h2>
            <div className="rounded-2xl overflow-hidden border border-background-200/70">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3975.349494569125!2d9.6872!3d4.0429!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1061137f1e2c5e6b%3A0x1e2c5e6b1061137f!2sBonanjo%2C%20Douala%2C%20Cameroun!5e0!3m2!1sfr!2scm!4v1700000000000"
                width="100%"
                height="400"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Tennis Club de Douala — Bonanjo, Douala"
                className="w-full"
              ></iframe>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <a href="https://wa.me/237687696969" target="_blank" rel="noopener noreferrer" className="fixed bottom-6 right-6 z-40 w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-full bg-[#25D366] text-white hover:bg-[#22c55e] transition-all duration-300 hover:scale-110 cursor-pointer" aria-label="Contact WhatsApp">
        <i className="ri-whatsapp-line text-2xl md:text-3xl"></i>
      </a>
    </main>
  );
}